// Чистата логика на справката „Разнесени отчети": филтрите в колоните
// (Разнесъл · Статус), търсенето и сумите. Сегментите Разнесени / Анулирани /
// Всички са пряк път към филтъра „Статус" — едно и също състояние. Справката
// се отваря на „Разнесени" (INITIAL_…); „Изчисти" води до празното = „Всички".
// Броячите са фасетни: по периода след търсенето и ОСТАНАЛИТЕ филтри.
import type { ReportPosting } from "../../../../api/inventory";
import { isPostingAnnulled } from "../../posting/components/reportPostingShared";
import type { FilterSpecs } from "../../components/filters/columnFilterModel";

export type PostingStatusKey = "all" | "posted" | "annulled";

export type PostingFilters = {
  users: string[];
  statuses: string[]; // posted | annulled
};

export const EMPTY_POSTING_FILTERS: PostingFilters = { users: [], statuses: [] };
export const INITIAL_POSTING_FILTERS: PostingFilters = { ...EMPTY_POSTING_FILTERS, statuses: ["posted"] };

export type PostingFilterKey = keyof PostingFilters;

export const postingUserOf = (posting: ReportPosting) => posting.createdByName || "—";
export const postingStatusOf = (posting: ReportPosting): Exclude<PostingStatusKey, "all"> =>
  isPostingAnnulled(posting) ? "annulled" : "posted";

// Сегментът е изгледът на филтъра „Статус": точно един → той, иначе „Всички"
export const postingSegmentOf = (statuses: string[]): PostingStatusKey =>
  statuses.length === 1 && (statuses[0] === "posted" || statuses[0] === "annulled") ? statuses[0] : "all";

export const postingFilterSpecs = ({
  titles,
  statusLabel,
}: {
  titles: Record<PostingFilterKey, string>;
  statusLabel: (value: string) => string;
}): FilterSpecs<ReportPosting, PostingFilters> => ({
  users: { kind: "list", title: titles.users, valuesOf: (posting) => [postingUserOf(posting)] },
  statuses: {
    kind: "list",
    title: titles.statuses,
    valuesOf: (posting) => [postingStatusOf(posting)],
    labelOf: statusLabel,
    order: ["posted", "annulled"],
  },
});

// Търсачката над таблицата: № на документа, „отчет №…", потребител, продукти
export const searchPostings = (postings: ReportPosting[], query: string) => {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return postings;
  return postings.filter((posting) => {
    const haystack = [
      `№${posting.id}`,
      posting.reportNumber != null ? `отчет №${posting.reportNumber}` : "",
      postingUserOf(posting),
      ...posting.lines.map((line) => line.productName),
    ]
      .join(" ")
      .toLowerCase();
    return words.every((word) => haystack.includes(word));
  });
};

// Има ли анулирани в целия филтриран резултат (не само във видимите преди
// „Покажи още") — тогава редът „Общо" казва, че те не влизат в сумата
export const hasAnnulledPostings = (postings: ReportPosting[]) => postings.some(isPostingAnnulled);

// Сумите и KPI — само разнесените (анулираните стоят зачертани)
export const postingTotals = (postings: ReportPosting[]) => {
  const active = postings.filter((posting) => !isPostingAnnulled(posting));
  return {
    count: active.length,
    products: active.reduce((sum, p) => sum + (p.productsCount || 0), 0),
    substitutions: active.reduce((sum, p) => sum + (p.substitutionsCount || 0), 0),
    net: active.reduce((sum, p) => sum + (p.totalValueNoVat || 0), 0),
    gross: active.reduce((sum, p) => sum + (p.totalValueWithVat || 0), 0),
  };
};
